// src/app/components/tabs/WhatsAppMediaPlayer.tsx
//
// Componentes de mídia reutilizados pelos bubbles do WhatsAppChatView.tsx:
// player de áudio customizado (estilo WhatsApp, com barra de progresso) e
// lightbox de imagem/vídeo em tela cheia com download.

import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Download, Loader2, Pause, Play, X } from 'lucide-react';

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ─── Audio player ───────────────────────────────────────────────────────────

interface AudioBubblePlayerProps {
  src: string;
  isOutgoing: boolean;
}

export function AudioBubblePlayer({ src, isOutgoing }: AudioBubblePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    const time = Number(e.target.value);
    setCurrentTime(time);
    if (audio) audio.currentTime = time;
  };

  const accentColor = isOutgoing ? '#0E9F6E' : '#5B5FEF';
  const displayTime = currentTime > 0 ? currentTime : duration;

  return (
    <div className="flex items-center gap-2 w-[220px] py-1">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        className="hidden"
      />
      <button
        type="button"
        onClick={togglePlay}
        className="size-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
        style={{ backgroundColor: accentColor }}
      >
        {isPlaying ? (
          <Pause className="size-3.5" fill="currentColor" />
        ) : (
          <Play className="size-3.5 ml-0.5" fill="currentColor" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          className="w-full h-1 cursor-pointer"
          style={{ accentColor }}
        />
        <span className="text-[10px] text-slate-400">{formatDuration(displayTime)}</span>
      </div>
    </div>
  );
}

// ─── Image / video lightbox ─────────────────────────────────────────────────

interface MediaLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: 'image' | 'video';
  /** Vazio enquanto o link decifrado ainda não chegou. */
  src: string;
  downloadName?: string;
  /** Miniatura borrada mostrada enquanto a imagem em alta carrega. */
  placeholderSrc?: string;
  failed?: boolean;
  onRetry?: () => void;
}

/**
 * Visualizador em tela cheia, no padrão do WhatsApp Web: fundo escuro, a mídia
 * inteira cabendo na tela (nunca cortada) e as ações numa barra própria —
 * o "fechar" é nosso, não o X padrão do Dialog, para não brigar com "Baixar".
 */
export function MediaLightbox({
  open, onOpenChange, kind, src, downloadName, placeholderSrc, failed = false, onRetry,
}: MediaLightboxProps) {
  const title = downloadName || (kind === 'image' ? 'Imagem' : 'Vídeo');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="w-auto max-w-[96vw] sm:max-w-[96vw] p-0 gap-0 flex flex-col bg-neutral-950 border-none rounded-lg overflow-hidden"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="flex items-center gap-2 pl-4 pr-2 h-11 bg-neutral-900">
          <span className="flex-1 min-w-0 text-sm text-white/80 truncate">{title}</span>
          {src && (
            <a
              href={src}
              download={downloadName}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              title="Baixar"
              aria-label="Baixar"
            >
              <Download className="size-4" />
            </a>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            title="Fechar (Esc)"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="relative flex items-center justify-center min-w-[min(320px,90vw)] min-h-[240px]">
          {src ? (
            kind === 'image' ? (
              <img src={src} alt={title} className="block max-h-[85vh] max-w-[96vw] object-contain" />
            ) : (
              <video src={src} controls autoPlay className="block max-h-[85vh] max-w-[96vw]" />
            )
          ) : (
            <>
              {placeholderSrc && (
                <img src={placeholderSrc} alt="" className="block w-[min(480px,90vw)] blur-md opacity-60" />
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 text-sm">
                {failed ? (
                  <>
                    <span>Não foi possível carregar.</span>
                    {onRetry && (
                      <button
                        type="button"
                        onClick={onRetry}
                        className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs"
                      >
                        Tentar de novo
                      </button>
                    )}
                  </>
                ) : (
                  <Loader2 className="size-7 animate-spin" />
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
